import cx from 'classnames'
import {
  type HTMLProps,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
} from 'react'

import Close from '../icons/Close'
import styles from './dialog.module.scss'
import {
  apply as applyDialogPolyfill,
  upgradeDialogElement,
} from './polyfill/dialog'

applyDialogPolyfill()

export type DialogProps = HTMLProps<HTMLDialogElement> & {
  isDismissable?: boolean
  closeButton?: boolean | ReactNode
  onDismiss?: (event?: Event) => void
}

export function Dialog({
  open,
  onDismiss,
  isDismissable = false,
  closeButton = isDismissable,
  children,
  className,
  ...props
}: DialogProps) {
  const dialogRef = useNativeDialog({ open, onDismiss, isDismissable })

  return (
    <dialog ref={dialogRef} className={cx(styles.dialog, className)} {...props}>
      {closeButton === true ? (
        <button
          type="button"
          className={styles.closeButton}
          onClick={() => dialogRef.current?.close()}
          aria-label="Close"
        >
          <Close width={24} />
        </button>
      ) : (
        closeButton
      )}
      {children}
    </dialog>
  )
}

function useNativeDialog({
  open,
  onDismiss,
  isDismissable,
}: {
  open?: boolean
  onDismiss?: (event?: Event) => void
  isDismissable?: boolean
} = {}) {
  const ref = useRef<HTMLDialogElement>(null)

  // Upgrade dialog element with polyfill if needed
  useEffect(() => {
    if (ref.current) {
      upgradeDialogElement(ref.current)
    }
  }, [])

  useOnNativeDialogDismissed(ref, onDismiss)
  useControlNativeDialog(ref, open, isDismissable)
  return ref
}

function useOnNativeDialogDismissed(
  ref: RefObject<HTMLDialogElement | null>,
  onDismiss?: (event?: Event) => void,
) {
  useEffect(() => {
    const dialog = ref.current
    if (!dialog || !onDismiss) return
    dialog.addEventListener('close', onDismiss)
    return () => dialog.removeEventListener('close', onDismiss)
  }, [onDismiss, ref])
}

function useControlNativeDialog(
  ref: RefObject<HTMLDialogElement | null>,
  open?: boolean,
  isDismissable?: boolean,
) {
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    dialog.closedBy = isDismissable ? 'closerequest' : 'none'
    dialog[open ? 'showModal' : 'close']()
  }, [ref, isDismissable, open])
}
